import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot, UrlTree } from '@angular/router';

import { Store } from '@ngrx/store';
import { SuiToastService } from '@semeris/ui';
import { isEqual } from 'lodash-es';
import { Observable, filter, map, of, take, timeout, withLatestFrom } from 'rxjs';

import { AppSelectors } from '../../../store/app/app.selectors';
import { UserProfileSelectors } from '../../../store/user-profile/user-profile.selectors';
import type { AssetWorkspace } from '../../home/models/asset-workspace.model';
import { workspaceRepickUrl } from '../../setup/workspace-repick';
import { isEmbedded } from '../constants/embedded.regex';
import { SemerisRoutes } from '../constants/routes';
import { WORKSPACE_URL_NAMESPACE, isWorkspaceScopedUrl } from '../constants/workspace-scoped-routes';
import { readTabWorkspace } from '../utils/tab-workspace-storage';

/** Cap on the init wait, matching `workspaceToolGuard` — a failed init must not hang the router. */
const INIT_WAIT_TIMEOUT_MS = 10_000;

/**
 * Makes `/ws/:workspaceId` authoritative: arriving at a workspace that isn't this tab's switches to
 * it, rather than showing that workspace's URL over another workspace's data.
 *
 * The switch is tab-local — bouncing through `/reload` dispatches `UseWorkspace` so this tab resets
 * without touching the stored default. Returning `true` would reuse the routed component, whose
 * `ngOnInit` would not re-run on the wiped store. The workspace list already carries each
 * workspace's assets, so no server round-trip is needed to make the switch.
 */
export const workspaceUrlGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
): Observable<boolean | UrlTree> | boolean => {
  // Embedded sessions never run app init, so the wait below would hang navigation.
  if (isEmbedded()) {
    return true;
  }

  const urlWorkspaceId = route.paramMap.get('workspaceId') ?? '';
  const store = inject(Store);
  const router = inject(Router);
  const toast = inject(SuiToastService);

  return store.select(AppSelectors.initialized).pipe(
    filter((initialized) => !!initialized),
    take(1),
    withLatestFrom(
      store.select(AppSelectors.selectedWorkspaceId),
      store.select(AppSelectors.selectedAssetShortNames),
      store.select(AppSelectors.availableWorkspaces),
      store.select(AppSelectors.workspaceGatewayUnavailable),
      store.select(UserProfileSelectors.profile),
    ),
    map(([, selectedWorkspaceId, selectedAssets, availableWorkspaces, gatewayUnavailable, profile]) => {
      if (!urlWorkspaceId) {
        return true;
      }

      const target = availableWorkspaces.find((workspace) => workspace.id === urlWorkspaceId);

      if (!target) {
        // A gateway outage hides every new-style workspace, so an unlisted id is not proof of
        // anything — saying "no access" there sends the user chasing a permission problem.
        if (gatewayUnavailable) {
          toast.warning(`Your workspaces couldn't be loaded, so that link can't be opened yet.`, {
            position: 'top-center',
            duration: 0,
            // A full load, not a router navigation: only a fresh boot re-reads the workspace list.
            actions: [{ label: 'Try again', onClick: () => window.location.assign(state.url) }],
          });
        } else {
          toast.warning(`You don't have access to the workspace in that link.`, { position: 'top-center' });
        }

        return ownWorkspaceHome(router, availableWorkspaces, [
          selectedWorkspaceId,
          readTabWorkspace()?.workspaceId,
          profile?.selectedWorkspaceId,
        ]);
      }

      // Assets are compared, not just the id: a link pasted into a fresh tab claims its workspace
      // with an empty asset list (nothing knew them yet), and matching on the id alone would leave
      // the tab stuck in that empty scope.
      const assetShortNames = target.assetShortNames ?? [];
      if (urlWorkspaceId === selectedWorkspaceId && isEqual(selectedAssets ?? [], assetShortNames)) {
        return true;
      }

      return router.createUrlTree(['/', SemerisRoutes.asset.changeAsset], {
        queryParams: {
          redirectTo: state.url,
          workspaceId: urlWorkspaceId,
          assetShortNames,
        },
      });
    }),
    timeout({ first: INIT_WAIT_TIMEOUT_MS, with: () => of(true) }),
  );
};

/**
 * Where a link into a workspace the user can't open lands: the home of the first candidate still on
 * offer, else the re-pick. Passing through `workspaceUrlGuard` again makes the switch tab-local.
 */
function ownWorkspaceHome(router: Router, availableWorkspaces: AssetWorkspace[], candidates: (string | undefined)[]): UrlTree {
  const workspaceId = candidates.find((id) => !!id && availableWorkspaces.some((workspace) => workspace.id === id));

  return workspaceId
    ? router.createUrlTree(['/', WORKSPACE_URL_NAMESPACE, workspaceId, SemerisRoutes.home.root])
    : workspaceRepickUrl(router);
}

/**
 * Canonicalises a link written before the workspace prefix existed: `/documents` becomes
 * `/ws/<active>/documents`. Sits on the `**` route, which is what an un-prefixed scoped URL now
 * falls through to; anything genuinely unknown passes through to the not-found screen.
 *
 * Permanent, not a migration shim — exported CSVs, third-party bookmarks and pasted links keep
 * arriving in the old shape indefinitely.
 */
export const unprefixedWorkspaceUrlGuard: CanActivateFn = (
  _route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
): Observable<boolean | UrlTree> | boolean => {
  if (isEmbedded() || !isWorkspaceScopedUrl(state.url)) {
    return true;
  }

  const store = inject(Store);
  const router = inject(Router);

  return store.select(AppSelectors.initialized).pipe(
    filter((initialized) => !!initialized),
    take(1),
    withLatestFrom(store.select(AppSelectors.selectedWorkspaceId)),
    // Without a workspace there is nothing to canonicalise with, and `/home` no longer exists
    // un-prefixed — so falling through would strand every guard's fallback on the not-found screen.
    map(([, workspaceId]) =>
      workspaceId ? router.parseUrl(`/${WORKSPACE_URL_NAMESPACE}/${workspaceId}${state.url}`) : workspaceRepickUrl(router),
    ),
    timeout({ first: INIT_WAIT_TIMEOUT_MS, with: () => of(true) }),
  );
};
